import type { MakeOptional } from '@/types';
import type {
  EdgeInstance,
  Graph,
  NodeId,
  NodeInstance,
} from '../../../types/graph';
import { TERMINAL_NODE_ID } from '../../../config/graph';
import { DeferredCompositeCommand } from '../composite';
import { AddEdgeCommand } from '../elementary';
import { createAddNodeCommand } from '../elementary/add-node';
import { createRemoveNodeCommand } from './remove-node';

export interface ReplaceGraphCommandProps {
  // Nodes of the new graph, with their ids. The terminal stays as it is.
  nodes: NodeInstance[];
  // Edges between the new nodes, and the one into the terminal.
  edges: MakeOptional<EdgeInstance, 'id' | 'order'>[];
}

/**
 * The nodes on the terminal, and any that are not connected. Removing these
 * with everything under them removes every node but the terminal.
 */
const topNodeIds = (graph: Readonly<Graph>): NodeId[] => {
  const parents = new Map(
    Object.values(graph.edges).map((edge) => [
      edge.from.nodeId,
      edge.to.nodeId,
    ]),
  );
  return Object.keys(graph.nodes).filter(
    (nodeId) =>
      nodeId !== TERMINAL_NODE_ID &&
      (parents.get(nodeId) ?? TERMINAL_NODE_ID) === TERMINAL_NODE_ID,
  );
};

/**
 * Replaces everything under the terminal with the given nodes and edges, as a
 * single command, so it is undone in one step. Removed nodes take the graphs of
 * their options with them, like removing them by hand.
 */
export const createReplaceGraphCommand = ({
  nodes,
  edges,
}: ReplaceGraphCommandProps) =>
  new DeferredCompositeCommand(
    (graph) => [
      ...topNodeIds(graph).map((nodeId) => createRemoveNodeCommand(nodeId)),
      ...nodes.map((node) => createAddNodeCommand(node)),
      ...edges.map((edge) => new AddEdgeCommand(edge)),
    ],
    'Replace graph',
  );
